import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiUrl } from '../config';
import { FiLock, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { sanitizeEmail, sanitizeText, validators } from '../utils/sanitize';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { FormField, FormAlert, formInputClass } from '../components/form';
import AuthSplitShell from '../components/auth/AuthSplitShell';

const ResetPassword = () => {
  const tc = useThemeClasses();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const token = searchParams.get('token') || '';
  const rawEmail = searchParams.get('email') || '';
  const email = sanitizeEmail(rawEmail);

  useEffect(() => {
    if (!token || !email) {
      setError('El enlace para restablecer la contraseña no es válido. Volvé a pedir uno nuevo.');
    }
  }, [token, email]);

  const inputWithIcon = (icon, inputProps, toggleProps) => (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{icon}</div>
      <input className={`${formInputClass(tc.inputClass)} pl-9 ${toggleProps ? 'pr-9' : ''}`} {...inputProps} />
      {toggleProps && (
        <button
          type="button"
          className={`absolute inset-y-0 right-0 pr-3 flex items-center ${tc.textMuted} hover:opacity-80`}
          {...toggleProps}
        />
      )}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token || !email) {
      setError('El enlace para restablecer la contraseña no es válido. Volvé a pedir uno nuevo.');
      return;
    }

    const passwordValidation = validators.password(password);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      return;
    }

    if (password !== repeatPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.message || 'No pudimos actualizar tu contraseña. Probá de nuevo en unos minutos.'
        );
      }
      setSuccess(
        data.message || 'Tu contraseña se actualizó correctamente. Ahora podés iniciar sesión.'
      );
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(sanitizeText(err.message) || 'Error de conexión. Verificá tu conexión a internet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitShell>
      <div className="mb-8">
        <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${tc.textPri}`}>
          Elegí una nueva contraseña
        </h2>
        <p className={`mt-2 text-sm ${tc.textMuted}`}>
          Por seguridad, vas a cerrar sesión en los dispositivos anteriores.
        </p>
      </div>

      {error && <FormAlert type="error">{sanitizeText(error)}</FormAlert>}
      {success && <FormAlert type="success">{sanitizeText(success)}</FormAlert>}

      <form onSubmit={handleSubmit} className="space-y-5">
        <FormField label="Nueva contraseña" required labelClass={tc.labelClass}>
          {inputWithIcon(
            <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
            {
              type: showPassword ? 'text' : 'password',
              value: password,
              onChange: (e) => setPassword(e.target.value),
              required: true,
              placeholder: 'Mínimo 6 caracteres',
            },
            {
              onClick: () => setShowPassword((v) => !v),
              tabIndex: -1,
              'aria-label': showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
              children: showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
            }
          )}
        </FormField>

        <FormField label="Repetir nueva contraseña" required labelClass={tc.labelClass}>
          {inputWithIcon(
            <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
            {
              type: showRepeatPassword ? 'text' : 'password',
              value: repeatPassword,
              onChange: (e) => setRepeatPassword(e.target.value),
              required: true,
              placeholder: 'Repetí la contraseña',
            },
            {
              onClick: () => setShowRepeatPassword((v) => !v),
              tabIndex: -1,
              'aria-label': showRepeatPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
              children: showRepeatPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
            }
          )}
        </FormField>

        <button
          type="submit"
          className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${tc.btnPrimary}`}
          disabled={loading || !token || !email}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Guardando...
            </>
          ) : (
            'Guardar nueva contraseña'
          )}
        </button>
      </form>

      <p className={`mt-6 text-sm ${tc.textSec}`}>
        <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-[#1F80FF] hover:underline">
          <FiArrowLeft className="w-3.5 h-3.5" />
          Volver a iniciar sesión
        </Link>
      </p>
    </AuthSplitShell>
  );
};

export default ResetPassword;
